/**
 * Canvases home sidebar — the navigation for the default landing surface.
 *
 *   canvases  (flat list, built-ins first)
 *   ──────────────────────────────────────
 *   chats     (history + New chat)   ← hub layout only
 *
 * Selecting a canvas sets ?cv=<slug>; selecting a chat (or New chat) flips the
 * main column to the docked chat (?chat=1). The chat rows are driven by the
 * chatBar ChatConsole publishes to dockStore — no chat state lives here. The
 * date-range picker + drawer-collapse control live in the shared sidebar
 * topbar above this body (same one Explore uses, minus the cluster selector).
 */
import { useEffect, useMemo, useState } from "react";
import ScrollArea from "./ScrollArea.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { NavSectionLabel, NavDivider } from "./ScopeSidebar.jsx";
import { FolderList } from "./StickyFolder.jsx";
import { FolderListRow } from "./SidebarNavRuntime.jsx";
import { orderCanvases } from "../lib/dashboardHome.js";
import { formatRelativeTime } from "./skills/skillBadges.jsx";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";

export default function CanvasMenu({
    canvases = [],
    selectedSlug,
    onSelect,
    // Hub layout adds the chat section below the canvas list.
    layout = "hub",
    // False when the caller lacks the chat's RBAC permission — drops the
    // whole Chats section (history + New chat) so no affordance points at
    // a console that isn't mounted.
    chatEnabled = true,
    chatBar = null,
    chatActive = false,
    onOpenChat,
    onNewChat,
}) {
    // Built-ins first (shipped order is preserved by the store), then the
    // tenant's own canvases — matches CanvasHomePage's first-canvas fallback.
    const ordered = useMemo(() => orderCanvases(canvases), [canvases]);

    // History = started chats only (blank drafts don't earn an entry until
    // their first message).
    const startedChats = (chatBar?.tabs || []).filter((t) => t.hasItems);

    // Coarse re-render tick so the relative-time notes age in place —
    // they're a pure render of chatBar, which is only republished when a
    // tab's identity fields change, so "just now" would otherwise stay
    // "just now" indefinitely.
    const [, setClockTick] = useState(0);
    useEffect(() => {
        if (!startedChats.length) return undefined;
        const t = setInterval(() => setClockTick((n) => n + 1), 60_000);
        return () => clearInterval(t);
    }, [startedChats.length]);

    return (
        <nav
            data-admin-menu
            className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]"
            aria-label="Canvases navigation"
        >
            <ScrollArea
                data-sidebar-scroll
                className="min-h-0 flex-1"
                contentClassName="flex min-h-full flex-col pt-[6px] pb-[12px]"
            >
                <NavSectionLabel>Dashboards</NavSectionLabel>
                <FolderList>
                    {ordered.length === 0 ? (
                        <div className="px-[12px] py-[10px] text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
                            No canvases yet.
                        </div>
                    ) : (
                        ordered.map((c) => (
                            <FolderListRow
                                key={c.id || c.slug}
                                as="button"
                                title={c.title || c.slug}
                                note={c.description ? <OverflowTooltip text={c.description} lines={2} /> : undefined}
                                active={!chatActive && c.slug === selectedSlug}
                                onClick={() => onSelect?.(c.slug)}
                            />
                        ))
                    )}
                </FolderList>

                {layout === "hub" && chatEnabled ? (
                    <>
                        <NavDivider />
                        <NavSectionLabel>Chats</NavSectionLabel>
                        <FolderList>
                            {startedChats.map((t) => (
                                <FolderListRow
                                    key={t.id}
                                    as="button"
                                    title={t.label || "New chat"}
                                    note={t.updatedAt ? formatRelativeTime(t.updatedAt) : undefined}
                                    active={chatActive && t.active}
                                    onClick={() => onOpenChat?.(t.id)}
                                />
                            ))}
                        </FolderList>
                        {/* New chat — always visible. If a blank draft is already
                            open it just focuses it (reuse), so it never stacks. */}
                        <button
                            type="button"
                            data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, ACTION.NEW_CHAT)}
                            onClick={() => onNewChat?.()}
                            className="mx-[6px] mt-[8px] flex items-center justify-center gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[7px] text-[12px] leading-none text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]"
                        >
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" className="h-[12px] w-[12px]"><path d="M8 3.5v9M3.5 8h9" /></svg>
                            New chat
                        </button>
                    </>
                ) : null}
            </ScrollArea>
        </nav>
    );
}
